import { format, subHours } from 'date-fns'

import { sleep } from '@last/core'

import { reportsApi } from '@/api/client'
import { useMoney } from '@/composables/useMoney'
import { t } from '@/i18n'
import Printer from '@/integrations/printer/printer'
import { useToast } from '@/plugins/toast/toast'
import { useCatalogStore } from '@/store/catalog'
import { useConfigStore } from '@/store/config'
import { useTabsStore } from '@/store/tabs'
import { useTillStore } from '@/store/till'
import sync from '@/sync/service'
import TicketGenerator from '@/tickets/generator'
import KitchenOrderFormatter from '@/tickets/kitchenOrderFormatter'
import type {
  Bill,
  BillWithPayments,
  CourierReport,
  KitchenOrder,
  Movement,
  Payment,
  ShiftMovement,
  StockItem,
  Tab,
  TillReport,
  XReport,
  ZReport
} from '@/types'
import type { EventName } from '@/types/events'

function hasDirectPrint() {
  const { config, device } = useConfigStore()
  return !config.onlyMasterPrinting || device.mode === 'master'
}

const wakeupCalls: Record<
  string,
  {
    valuesToCheck: string[]
    meta: boolean
    number: boolean
    wakeUp: () => void
    [key: string]: boolean | any
  }
> = {}

function wakeupPrint(billId: string, value: string) {
  const call = wakeupCalls[billId]
  if (call) {
    call[value] = true
    if (call.valuesToCheck.every(val => call[val])) {
      call.wakeUp()
    }
  }
}

// https://stackoverflow.com/a/51843228
function wakeablePrintPromise(bill: BillWithPayments | Bill, valuesToCheck: string[]) {
  return new Promise(resolve => {
    wakeupCalls[bill.id] = {
      meta: !!bill.metadata,
      number: !!bill.number,
      valuesToCheck,
      wakeUp: () => resolve(true)
    }
  })
}

async function printKitchenOrder(order: KitchenOrder) {
  const toast = useToast()
  toast({
    title: t('notifications.sent-to-kitchen')
  })
  if (!hasDirectPrint()) {
    sync.record('kitchenOrderAddedToPrintQueue', order.id)
    return
  }
  const tabsStore = useTabsStore()
  const tab = tabsStore.tabs[order.tabId]
  const catalog = useCatalogStore()
  const catalogId = catalog.getCatalogIdByTabVirtualBrandId(tab.virtualBrandId)
  const sortedCourses = catalog.catalogs[catalogId!]?.courses || []
  const config = useConfigStore()
  const printer = config.printers[order.printerId] || {}
  const scale = 4
  const canvas = await TicketGenerator.kitchenOrder(
    KitchenOrderFormatter.format({ ...order, tableName: tab.tableName }, sortedCourses),
    printer,
    scale
  )

  let printedEvent = null
  printedEvent = {
    name: 'kitchenOrderPrinted' as const,
    data: { orderId: order.id }
  }
  Printer.printImage(order.printerId, canvas, order.copies, printedEvent)
}

async function printKitchenNote(courseLabel: string, tab: Tab) {
  const scale = 4
  const canvas = await TicketGenerator.kitchenNote(courseLabel, tab, scale)

  const tabs = useTabsStore()
  const kitchenOrdersOnTab = tab.kitchenOrders.map(order => {
    return tabs.kitchenOrders[order]
  })

  const kitchenOrdersForCourse = kitchenOrdersOnTab.filter(order => {
    return order.versions.some(version => {
      return version.products.some(product => product.course === courseLabel)
    })
  })

  const printers = [
    ...new Set(
      kitchenOrdersForCourse.map(order => {
        return order.printerId
      })
    )
  ]

  printers.forEach(printerId => Printer.printImage(printerId, canvas, 1))
}

async function printCancelTabTicket(tab: Tab) {
  const scale = 4
  const canvas = await TicketGenerator.cancelTab(tab, scale)
  const tabs = useTabsStore()
  const printers = [
    ...new Set(
      tab.kitchenOrders.map(order => {
        return tabs.kitchenOrders[order]?.printerId
      })
    )
  ]
  printers.forEach(printerId => Printer.printImage(printerId, canvas, 1))
}

async function printBill(
  bill: BillWithPayments,
  isPendingBill = false,
  {
    hospitality
  }: {
    hospitality?: boolean
  } = { hospitality: false }
) {
  const { currency } = useMoney()
  const toast = useToast()
  toast({
    title: t('notifications.printing-bill', {
      total: currency(bill.total)
    })
  })
  const { config, device } = useConfigStore()
  if (!hasDirectPrint() && !device.useSelfPrinter) {
    if (!isPendingBill) {
      sync.record('billAddedToPrintQueue', { billId: bill.id, hospitality })
      return
    } else {
      sync.record('pendingBillAddedToPrintQueue', bill.tabId)
      return
    }
  }

  const valuesToCheck = []
  if (config.waitForMetaBeforePrintingBill && !bill.metadata) valuesToCheck.push('meta')
  if (config.organizationConfig.waitForNumberBeforePrintingBill && !bill.number)
    valuesToCheck.push('number')
  if (!isPendingBill && valuesToCheck.length > 0) {
    await Promise.race([wakeablePrintPromise(bill, valuesToCheck), sleep(5000)])
    delete wakeupCalls[bill.id]
    const tabs = useTabsStore()
    const storedBill = tabs.getBillById(bill.id)
    bill = storedBill || bill
  }
  const options = {
    barcode: config.organizationConfig.barcodes,
    logoImageId: config.virtualBrands.find(v => v.id === bill.virtualBrandId)?.imageId,
    isTaxExempt: config.legalEntity.isTaxExempt,
    taxExemptionReason: config.legalEntity.taxExemptionReason,
    hospitality
  }
  const scale = 4
  const canvas = await TicketGenerator.bill(bill, options, scale)
  const printerId = getTargetPrinterId(!!bill.pickupType)
  let printedEvent = undefined
  if (!bill.printedTime && !isPendingBill) {
    printedEvent = {
      name: 'billPrinted',
      data: { billId: bill.id }
    } satisfies {
      name: EventName
      data: any
    }
  }
  if (!printerId) return
  Printer.printImage(printerId, canvas, config.billCopies, printedEvent)
}

async function printCourierReport(report: CourierReport & { total: number; courierName: string }) {
  const scale = 4
  const canvas = await TicketGenerator.courierReport(report, scale)

  const printerId = getTargetPrinterId()
  if (!printerId) return
  Printer.printImage(printerId, canvas, 1)
}

async function printXReport(report?: XReport | null) {
  const toast = useToast()
  toast({ title: t('notifications.x-report') })
  const scale = 4
  if (!report) {
    report = await reportsApi.getXReport()
  }
  const config = useConfigStore()
  const canvas = await TicketGenerator.xReport(report, config.config, scale)
  const printerId = getTargetPrinterId()
  if (!printerId) return
  Printer.printImage(printerId, canvas, 1)
}

async function printZReport(report?: ZReport | null) {
  const scale = 4
  if (!report) {
    report = await reportsApi.getZReport()
  }
  const config = useConfigStore()
  const canvas = await TicketGenerator.zReport(report, config.config, scale)

  const printerId = getTargetPrinterId()
  if (!printerId) return
  Printer.printImage(printerId, canvas, 1)
}

async function printRealTimeReport() {
  const toast = useToast()
  toast({
    title: t('notifications.real-time-report')
  })
  const today = subHours(new Date(), 4)
  const report = await reportsApi.getZReport({
    startDate: format(today, 'yyyy-MM-dd'),
    endDate: format(today, 'yyyy-MM-dd')
  })
  const scale = 4
  const config = useConfigStore()
  const canvas = await TicketGenerator.realTimeReport(report, config.config, scale)
  const printerId = getTargetPrinterId()
  if (!printerId) return
  Printer.printImage(printerId, canvas, 1)
}

async function printTillReport(report: TillReport, tillName: string) {
  const toast = useToast()
  toast({
    title: t('notifications.till-report')
  })
  const scale = 4
  const canvas = await TicketGenerator.tillReport(report, tillName, scale)
  const printerId = getTargetPrinterId()
  if (!printerId) return
  Printer.printImage(printerId, canvas, 1)
}

function getTargetPrinterId(isDelivery?: boolean) {
  let printerId
  const { config, device, currentAdyenDataphone, tills } = useConfigStore()
  const till = useTillStore()
  if (isDelivery) {
    if (config.deliveryPrinterId) {
      printerId = config.deliveryPrinterId
    } else {
      const deliveryTillId = config.deliveryTillId
      const cashTill = tills.find(t => t.id === deliveryTillId)
      if (cashTill) {
        printerId = cashTill.printerId
      }
    }
  }
  if (!printerId) {
    if (till.selectedCashTill && till.selectedCashTill.printerId) {
      printerId = till.selectedCashTill.printerId
    } else {
      printerId = config.defaultBillPrinter
    }
  }
  if (currentAdyenDataphone && device.useSelfPrinter) {
    printerId = 'self'
  }
  return printerId
}

async function printPayInPayOut(movement: Movement & { employee?: string; date: Date }) {
  const scale = 4
  const canvas = await TicketGenerator.payInPayOut(movement, scale)
  const printerId = getTargetPrinterId()
  if (!printerId) return
  Printer.printImage(printerId, canvas, 1)
}

async function printShiftMovements(
  tillId: string,
  name: string,
  movements: ShiftMovement[] | null
) {
  const toast = useToast()
  toast({
    title: t('notifications.print-movements')
  })
  if (!movements) {
    const response = await reportsApi.getLastOpenShiftMovements({ tillId })
    movements = response.movements
  }
  const scale = 4
  const config = useConfigStore()
  const canvas = await TicketGenerator.shiftMovements({ movements }, name, config.config, scale)
  const printerId = getTargetPrinterId()
  if (!printerId) return
  Printer.printImage(printerId, canvas, 1)
}

async function printBankReceipt(payment: Payment) {
  if (!hasDirectPrint()) {
    sync.record('paymentAddedToPrintQueue', payment.id)
    return
  }
  const scale = 4
  const canvas = await TicketGenerator.bankReceipt(payment, scale)
  const printerId = getTargetPrinterId()
  if (!printerId) return
  Printer.printImage(printerId, canvas, 1)
}

async function printStockReport(items: StockItem[]) {
  const scale = 4
  const canvas = await TicketGenerator.stockReport(items, scale)
  const printerId = getTargetPrinterId()
  if (!printerId) return
  Printer.printImage(printerId, canvas, 1)
}

export default {
  printKitchenOrder,
  printKitchenNote,
  printCancelTabTicket,
  printBill,
  printZReport,
  printXReport,
  printTillReport,
  printPayInPayOut,
  printShiftMovements,
  printRealTimeReport,
  printCourierReport,
  printBankReceipt,
  printStockReport,
  wakeupPrint
}
